<template>
  <div>
    <template v-if="loading">
      <v-card class="app-card"
              v-bind:rounded="$vuetify.breakpoint.mdAndDown ? null : 'lg'"
              v-bind:tile="$vuetify.breakpoint.mdAndDown">
        <v-skeleton-loader type="list-item-avatar-two-line@8" />
      </v-card>
    </template>
    <template v-else>
      <v-card class="app-card mb-4"
              hover
              v-bind:rounded="$vuetify.breakpoint.mdAndDown ? null : 'lg'"
              v-bind:tile="$vuetify.breakpoint.mdAndDown"
              v-bind:key="foodTruck.id"
              v-for="foodTruck in highlightedFoodTrucks"
              v-on:click="$emit('food-truck-selected', foodTruck.id )">
        <v-img class="align-end white--text"
               height="220px"
               gradient="to top, rgba(0, 0, 0, .8) 0%, rgba(0, 0, 0, 0) 60%"
               v-bind:src="foodTruck.imageId != null ? attachmentUrl(foodTruck.imageId) : defaultImageUrl">
          <v-chip class="highlight-status"
                  dark
                  small
                  v-bind:color="foodTruck.open ? 'open' : 'closed'">
            {{ foodTruck.open ? $t('openToday') : $t('closedToday') }}
          </v-chip>
          <v-card-title class="flex-nowrap">
            <v-avatar class="mr-4"
                      color="white"
                      size="48"
                      v-if="foodTruck.logoId != null">
              <v-img v-bind:src="attachmentUrl(foodTruck.logoId)" />
            </v-avatar>
            <div class="highlight-text">
              <div class="font-weight-bold text-truncate">{{ foodTruck.title }}</div>
              <div class="text-body-2 text-truncate">{{ foodTruck.regionName || foodTruck.city }}</div>
            </div>
          </v-card-title>
        </v-img>
      </v-card>
      <v-card class="app-card"
              v-bind:rounded="$vuetify.breakpoint.mdAndDown ? null : 'lg'"
              v-bind:tile="$vuetify.breakpoint.mdAndDown">
        <v-text-field clearable
                      flat
                      hide-details="auto"
                      prepend-inner-icon="mdi-magnify"
                      solo
                      v-bind:label="$t('search')"
                      v-model="search" />
        <v-divider />
        <div class="empty-state"
             v-if="enrichedFoodTrucks.length === 0">
          <v-icon class="mb-2"
                  large>
            mdi-truck-remove-outline
          </v-icon>
          <p class="mb-0">{{ hasSearch ? $t('noResults', { search }) : $t('noEntries') }}</p>
        </div>
        <v-list class="py-0">
          <template v-for="(group, index) of remainingFoodTruckGroups">
            <v-subheader class="font-weight-bold"
                         inset
                         v-bind:key="`group-${index}-subheader`">
              {{ regionGroupTitle(group.regionGroup) }}
            </v-subheader>

            <template v-for="(foodTruck, index) in group.foodTrucks">
              <v-list-item exact
                           v-bind:key="`${foodTruck.id}-list_item`"
                           v-on:click="$emit('food-truck-selected', foodTruck.id )">
                <v-list-item-avatar v-bind:color="foodTruck.logoId == null ? 'accent' : null">
                  <v-img v-bind:src="attachmentUrl(foodTruck.logoId)"
                         v-if="foodTruck.logoId != null" />
                  <v-icon color="white"
                          dark
                          v-else>
                    mdi-truck
                  </v-icon>
                </v-list-item-avatar>
                <v-list-item-content>
                  <v-list-item-title>{{ foodTruck.title }}</v-list-item-title>
                  <v-list-item-subtitle>{{ foodTruck.specialties || foodTruck.city || categoryTitle(foodTruck.categoryId) }}</v-list-item-subtitle>
                </v-list-item-content>
                <v-list-item-action class="align-center flex-row"
                                    v-if="foodTruck.open">
                  <v-chip color="open"
                          dark
                          small
                          v-if="$vuetify.breakpoint.smAndUp">
                    {{ $t('openToday') }}
                  </v-chip>
                  <span class="open status-dot"
                        v-bind:aria-label="$t('openToday')"
                        v-bind:title="$t('openToday')"
                        v-else />
                </v-list-item-action>
                <v-list-item-icon class="align-self-center ml-2">
                  <v-icon color="grey lighten-1">mdi-chevron-right</v-icon>
                </v-list-item-icon>
              </v-list-item>
              <v-divider inset
                         v-bind:key="`${foodTruck.id}-divider`"
                         v-if="index !== group.foodTrucks.length - 1" />
            </template>

            <v-divider v-bind:key="`group-${index}-divider`"
                       v-if="index !== remainingFoodTruckGroups.length - 1" />
          </template>
        </v-list>
      </v-card>
    </template>
  </div>
</template>

<script>
  import moment from 'moment'

  import { mapGetters } from 'vuex'

  export default {
    computed: {
      remainingFoodTruckGroups() {
        return this.remainingFoodTrucks.reduce((result, current) => {
          const index = result.findIndex(g => g.regionGroup === current.regionGroup)

          if (index !== -1) {
            result[index].foodTrucks.push(current)
          } else {
            result.push({
              regionGroup: current.regionGroup,
              foodTrucks: [ current ]
            })
          }

          return result
        }, []).sort((a, b) => a.regionGroup?.localeCompare(b.regionGroup) ?? 1)
      },
      remainingFoodTrucks() {
        return this.enrichedFoodTrucks.filter(e => !e.highlight)
      },
      enrichedFoodTrucks() {
        let filteredFoodTrucks = this.foodTrucks.filter(f => !f.inactive)

        if (this.search != null && this.search.length !== 0) {
          filteredFoodTrucks = filteredFoodTrucks.filter(f => f.title.toLowerCase().includes(this.search.toLowerCase()))
        }

        return filteredFoodTrucks.map(f => {
          const dayOfWeek = moment().isoWeekday()
          const week = moment().isoWeek()
          const isEvenWeek = week % 2 === 0
          const isOddWeek = week % 2 === 1

          return {
            ...f,
            open: f.timeSlots.some(t => !t.disabled && t.dayOfWeek === dayOfWeek && (t.evenWeeks && isEvenWeek || t.oddWeeks && isOddWeek))
          }
        }).sort((a, b) => b.open - a.open)
      },
      hasSearch() {
        return this.search != null && this.search.length !== 0
      },
      highlightedFoodTrucks() {
        return this.enrichedFoodTrucks.filter(e => e.highlight)
      },
      ...mapGetters([
        'attachmentUrl',
        'categoryTitle'
      ])
    },
    data: () => ({
      defaultImageUrl: `${process.env.BASE_URL}images/food-truck-default-image.jpg`,
      search: null
    }),
    methods: {
      regionGroupTitle(regionGroup) {
        if (regionGroup == null || regionGroup.length === 0) {
          return this.$t('other')
        }

        return regionGroup
      }
    },
    name: 'FoodTruckListCard',
    props: {
      foodTrucks: Array,
      loading: Boolean
    }
  }
</script>

<style scoped>
  .highlight-status {
    position: absolute;
    right: 12px;
    top: 12px;
  }

  .highlight-text {
    line-height: 1.3;
    min-width: 0;
  }

  .status-dot {
    border-radius: 50%;
    display: inline-block;
    height: 10px;
    width: 10px;
  }
</style>

<i18n>
  {
    "de": {
      "closedToday": "Heute geschlossen",
      "noEntries": "Noch keine Einträge vorhanden.",
      "noResults": "Keine Treffer für „{search}“.",
      "openToday": "Heute geöffnet",
      "other": "Sonstige",
      "search": "Suche"
    },
    "en": {
      "closedToday": "Closed today",
      "noEntries": "No entries yet.",
      "noResults": "No results for “{search}”.",
      "openToday": "Open today",
      "other": "Other",
      "search": "Search"
    }
  }
</i18n>